/* istanbul ignore file */
import { createAction } from '@reduxjs/toolkit';
import type {
    IAttachmentIdRead,
    IAttachmentRead,
    IMessage,
    IMessageParams,
    INewMessage,
    ISecureInboxSettings,
    ISecureInboxTopic,
    IUserMailbox,
} from '../../api/domain';

enum SecureInboxApiActions {
    ARCHIVE_MESSAGE = 'secinbox.api.archive-message',
    ARCHIVE_MESSAGE_FAILURE = 'secinbox.api.archive-message.failure',
    ARCHIVE_MESSAGE_SUCCESS = 'secinbox.api.archive-message.success',
    ARCHIVE_MULTIPLE_MESSAGES = 'secinbox.api.archive-messages',
    ARCHIVE_MULTIPLE_MESSAGES_FAILURE = 'secinbox.api.archive-messages.failure',
    ARCHIVE_MULTIPLE_MESSAGES_SUCCESS = 'secinbox.api.archive-messages.success',
    DELETE_ATTACHMENT = 'secinbox.api.delete-attachment',
    DELETE_ATTACHMENT_SUCCESS = 'secinbox.api.delete-attachment.success',
    DELETE_MESSAGE = 'secinbox.api.delete-message',
    DELETE_MESSAGE_FAILURE = 'secinbox.api.delete-message.failure',
    DELETE_MESSAGE_SUCCESS = 'secinbox.api.delete-message.success',
    DELETE_MULTIPLE_MESSAGES = 'secinbox.api.delete-messages',
    DELETE_MULTIPLE_MESSAGES_FAILURE = 'secinbox.api.delete-messages.failure',
    DELETE_MULTIPLE_MESSAGES_SUCCESS = 'secinbox.api.delete-messages.success',
    DOWNLOAD_ATTACHMENT = 'secinbox.api.download.attachment',
    DOWNLOAD_ATTACHMENT_FAILURE = 'secinbox.api.download.attachment.failure',
    DOWNLOAD_ATTACHMENT_SUCCESS = 'secinbox.api.download.attachment.success',
    FETCH_ATTACHMENTS_SUCCESS = 'secinbox.api.fetch.attachments.success',
    FETCH_ATTACHMENT_CONTENT = 'secinbox.api.fetch.attachment-content',
    FETCH_ATTACHMENT_CONTENT_FAILURE = 'secinbox.api.fetch.attachment-content.failure',
    FETCH_ATTACHMENT_CONTENT_SUCCESS = 'secinbox.api.fetch.attachment-content.success',
    FETCH_MAILBOXES = 'secinbox.api.fetch.mailboxes',
    FETCH_MAILBOXES_FAILURE = 'secinbox.api.fetch.mailboxes.failure',
    FETCH_MAILBOXES_SUCCESS = 'secinbox.api.fetch.mailboxes.success',
    FETCH_MESSAGE = 'secinbox.api.fetch.message',
    FETCH_MESSAGES = 'secinbox.api.fetch.messages',
    FETCH_MESSAGES_FAILURE = 'secinbox.api.fetch.messages.failure',
    FETCH_MESSAGES_SUCCESS = 'secinbox.api.fetch.messages.success',
    FETCH_MESSAGE_FAILURE = 'secinbox.api.fetch.message.failure',
    FETCH_MESSAGE_SUCCESS = 'secinbox.api.fetch.message.success',
    FETCH_SETTINGS = 'secinbox.api.fetch.settings',
    FETCH_SETTINGS_FAILURE = 'secinbox.api.fetch.settings.failure',
    FETCH_SETTINGS_SUCCESS = 'secinbox.api.fetch.settings.success',
    FETCH_TOPICS = 'secinbox.api.fetch.topics',
    FETCH_TOPICS_FAILURE = 'secinbox.api.fetch.topics.failure',
    FETCH_TOPICS_SUCCESS = 'secinbox.api.fetch.topics.success',
    FETCH_UNREAD_COUNT = 'secinbox.api.fetch.unread-count',
    FETCH_UNREAD_COUNT_FAILURE = 'secinbox.api.fetch.unread-count.failure',
    FETCH_UNREAD_COUNT_SUCCESS = 'secinbox.api.fetch.unread-count.success',
    READ_MESSAGE = 'secinbox.api.read-message',
    READ_MESSAGE_FAILURE = 'secinbox.api.read-message.failure',
    READ_MESSAGE_SUCCESS = 'secinbox.api.read-message.success',
    RESOLVE_RM_FOR_MAILBOXES = 'secinbox.api.resolve-rm-for-mailboxes',
    RESOLVE_RM_FOR_MAILBOXES_FAILURE = 'secinbox.api.resolve-rm-for-mailboxes.failure',
    RESOLVE_RM_FOR_MAILBOXES_SUCCESS = 'secinbox.api.resolve-rm-for-mailboxes.success',
    RESOLVE_MESSAGES_ATTACHMENTS = 'secinbox.api.resolve-messages-attachments',
    SEND_MESSAGE = 'secinbox.api.send-message',
    SEND_MESSAGE_FAILURE = 'secinbox.api.send-message.failure',
    SEND_MESSAGE_OUTSIDE_INBOX = 'secinbox.api.send-message-outside-inbox',
    SEND_MESSAGE_SUCCESS = 'secinbox.api.send-message.success',
    UNARCHIVE_MESSAGE = 'secinbox.api.unarchive-message',
    UNARCHIVE_MESSAGE_FAILURE = 'secinbox.api.unarchive-message.failure',
    UNARCHIVE_MESSAGE_SUCCESS = 'secinbox.api.unarchive-message.success',
    UNARCHIVE_MULTIPLE_MESSAGES = 'secinbox.api.unarchive-messages',
    UNARCHIVE_MULTIPLE_MESSAGES_FAILURE = 'secinbox.api.unarchive-messages.failure',
    UNARCHIVE_MULTIPLE_MESSAGES_SUCCESS = 'secinbox.api.unarchive-messages.success',
    UPDATE_MESSAGE = 'secinbox.api.update.message',
    QUEUE_MESSAGE = 'secinbox.api.queue-message',
    UPLOAD_ATTACHMENTS = 'secinbox.api.upload-attachments',
    UPLOAD_ATTACHMENTS_FAILURE = 'secinbox.api.upload-attachments.failure',
    UPLOAD_ATTACHMENTS_SUCCESS = 'secinbox.api.upload-attachments.success',
}

export const archiveMessage = createAction<{ message: IMessage; params: IMessageParams }>(SecureInboxApiActions.ARCHIVE_MESSAGE);
export const archiveMessageFailure = createAction(SecureInboxApiActions.ARCHIVE_MESSAGE_FAILURE);
export const archiveMessageSuccess = createAction<IMessageParams>(SecureInboxApiActions.ARCHIVE_MESSAGE_SUCCESS);
export const archiveMultipleMessages = createAction<{ messages: IMessage[]; params: IMessageParams }>(SecureInboxApiActions.ARCHIVE_MULTIPLE_MESSAGES);
export const archiveMultipleMessagesFailure = createAction(SecureInboxApiActions.ARCHIVE_MULTIPLE_MESSAGES_FAILURE);
export const archiveMultipleMessagesSuccess = createAction<IMessageParams>(SecureInboxApiActions.ARCHIVE_MULTIPLE_MESSAGES_SUCCESS);
export const deleteAttachment = createAction<{ messageId: string; attachmentId: string }>(SecureInboxApiActions.DELETE_ATTACHMENT);
export const deleteAttachmentSuccess = createAction<{ messageId: string; attachmentId: string }>(SecureInboxApiActions.DELETE_ATTACHMENT_SUCCESS);
export const deleteMessage = createAction<{ message: IMessage; params: IMessageParams }>(SecureInboxApiActions.DELETE_MESSAGE);
export const deleteMessageFailure = createAction(SecureInboxApiActions.DELETE_MESSAGE_FAILURE);
export const deleteMessageSuccess = createAction<{ attachmentIds?: IAttachmentIdRead[]; messageId: string; params: IMessageParams }>(
    SecureInboxApiActions.DELETE_MESSAGE_SUCCESS,
);
export const deleteMultipleMessages = createAction<{ messages: IMessage[]; params: IMessageParams }>(SecureInboxApiActions.DELETE_MULTIPLE_MESSAGES);
export const deleteMultipleMessagesFailure = createAction(SecureInboxApiActions.DELETE_MULTIPLE_MESSAGES_FAILURE);
export const deleteMultipleMessagesSuccess = createAction<{ messages: IMessage[]; params: IMessageParams }>(
    SecureInboxApiActions.DELETE_MULTIPLE_MESSAGES_SUCCESS,
);
export const downloadAttachment = createAction<{ attachmentId: string }>(SecureInboxApiActions.DOWNLOAD_ATTACHMENT);
export const downloadAttachmentFailure = createAction(SecureInboxApiActions.DOWNLOAD_ATTACHMENT_FAILURE);
export const downloadAttachmentSuccess = createAction(SecureInboxApiActions.DOWNLOAD_ATTACHMENT_SUCCESS);
export const fetchAttachmentsSuccess = createAction<IAttachmentRead[]>(SecureInboxApiActions.FETCH_ATTACHMENTS_SUCCESS);
export const fetchAttachmentContent = createAction<{ attachmentId: string }>(SecureInboxApiActions.FETCH_ATTACHMENT_CONTENT);
export const fetchAttachmentContentSuccess = createAction<{ attachmentId: string; fileSize: number }>(SecureInboxApiActions.FETCH_ATTACHMENT_CONTENT_SUCCESS);
export const fetchAttachmentContentFailure = createAction(SecureInboxApiActions.FETCH_ATTACHMENT_CONTENT_FAILURE);
export const fetchMailboxes = createAction<string[] | undefined>(SecureInboxApiActions.FETCH_MAILBOXES);
export const fetchMailboxesFailure = createAction(SecureInboxApiActions.FETCH_MAILBOXES_FAILURE);
export const fetchMailboxesSuccess = createAction<IUserMailbox[]>(SecureInboxApiActions.FETCH_MAILBOXES_SUCCESS);
export const fetchMessage = createAction<{ messageId: string }>(SecureInboxApiActions.FETCH_MESSAGE);
export const fetchMessageFailure = createAction(SecureInboxApiActions.FETCH_MESSAGE_FAILURE);
export const fetchMessageSuccess = createAction<IMessage>(SecureInboxApiActions.FETCH_MESSAGE_SUCCESS);
export const fetchMessages = createAction<IMessageParams>(SecureInboxApiActions.FETCH_MESSAGES);
export const fetchMessagesFailure = createAction(SecureInboxApiActions.FETCH_MESSAGES_FAILURE);
export const fetchMessagesSuccess = createAction<{ messages: IMessage[]; totalCount: number }>(SecureInboxApiActions.FETCH_MESSAGES_SUCCESS);
export const fetchSettings = createAction(SecureInboxApiActions.FETCH_SETTINGS);
export const fetchSettingsFailure = createAction(SecureInboxApiActions.FETCH_SETTINGS_FAILURE);
export const fetchSettingsSuccess = createAction<ISecureInboxSettings>(SecureInboxApiActions.FETCH_SETTINGS_SUCCESS);
export const fetchTopics = createAction(SecureInboxApiActions.FETCH_TOPICS);
export const fetchTopicsFailure = createAction(SecureInboxApiActions.FETCH_TOPICS_FAILURE);
export const fetchTopicsSuccess = createAction<ISecureInboxTopic[]>(SecureInboxApiActions.FETCH_TOPICS_SUCCESS);
export const fetchUnreadCount = createAction(SecureInboxApiActions.FETCH_UNREAD_COUNT);
export const fetchUnreadCountFailure = createAction(SecureInboxApiActions.FETCH_UNREAD_COUNT_FAILURE);
export const fetchUnreadCountSuccess = createAction<number>(SecureInboxApiActions.FETCH_UNREAD_COUNT_SUCCESS);
export const queueMessage = createAction<{ messageId: string }>(SecureInboxApiActions.QUEUE_MESSAGE);
export const readMessage = createAction<IMessage>(SecureInboxApiActions.READ_MESSAGE);
export const readMessageFailure = createAction(SecureInboxApiActions.READ_MESSAGE_FAILURE);
export const readMessageSuccess = createAction(SecureInboxApiActions.READ_MESSAGE_SUCCESS);
export const resolveMessagesAttachments = createAction<{ messages: IMessage[] }>(SecureInboxApiActions.RESOLVE_MESSAGES_ATTACHMENTS);
export const resolveRmForMailboxes = createAction(SecureInboxApiActions.RESOLVE_RM_FOR_MAILBOXES);
export const resolveRmForMailboxesFailure = createAction(SecureInboxApiActions.RESOLVE_RM_FOR_MAILBOXES_FAILURE);
export const resolveRmForMailboxesSuccess = createAction<IUserMailbox[]>(SecureInboxApiActions.RESOLVE_RM_FOR_MAILBOXES_SUCCESS);
export const sendMessage = createAction<{
    mailboxId: string;
    newMessage: INewMessage;
    attachmentsToBeUploaded: File[];
    vaultId: string;
}>(SecureInboxApiActions.SEND_MESSAGE);
export const sendMessageFailure = createAction(SecureInboxApiActions.SEND_MESSAGE_FAILURE);
export const sendMessageOutsideInbox = createAction<{ mailboxId: string; newMessage: INewMessage }>(SecureInboxApiActions.SEND_MESSAGE_OUTSIDE_INBOX);
export const sendMessageSuccess = createAction(SecureInboxApiActions.SEND_MESSAGE_SUCCESS);
export const unarchiveMessage = createAction<{ message: IMessage; params: IMessageParams }>(SecureInboxApiActions.UNARCHIVE_MESSAGE);
export const unarchiveMessageFailure = createAction(SecureInboxApiActions.UNARCHIVE_MESSAGE_FAILURE);
export const unarchiveMessageSuccess = createAction<IMessageParams>(SecureInboxApiActions.UNARCHIVE_MESSAGE_SUCCESS);
export const unarchiveMultipleMessages = createAction<{ messages: IMessage[]; params: IMessageParams }>(SecureInboxApiActions.UNARCHIVE_MULTIPLE_MESSAGES);
export const unarchiveMultipleMessagesFailure = createAction(SecureInboxApiActions.UNARCHIVE_MULTIPLE_MESSAGES_FAILURE);
export const unarchiveMultipleMessagesSuccess = createAction<IMessageParams>(SecureInboxApiActions.UNARCHIVE_MULTIPLE_MESSAGES_SUCCESS);
export const updateMessage = createAction<IMessage>(SecureInboxApiActions.UPDATE_MESSAGE);
export const uploadAttachment = createAction<{ attachmentContent: Blob; attachmentName: string; messageId: string }>(SecureInboxApiActions.UPLOAD_ATTACHMENTS);
export const uploadAttachmentFailure = createAction(SecureInboxApiActions.UPLOAD_ATTACHMENTS_FAILURE);
export const uploadAttachmentSuccess = createAction(SecureInboxApiActions.UPLOAD_ATTACHMENTS_SUCCESS);
